import type { RenderArmyReadinessReportToolOutput } from '@azymmetric/shared/tools/render-army-readiness-report.tool-config';
import type {
  NavyReadinessTier,
  RenderNavyReadinessReportToolOutput,
} from '@azymmetric/shared/tools/render-navy-readiness-report.tool-config';
import type { RenderSpaceForceReadinessReportToolOutput } from '@azymmetric/shared/tools/render-space-force-readiness-report.tool-config';
import { cn } from '@lib/utils';
import { type FC, Fragment, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

type NavyReadinessReportArtifactProps = {
  report:
    | RenderArmyReadinessReportToolOutput
    | RenderNavyReadinessReportToolOutput
    | RenderSpaceForceReadinessReportToolOutput;
};

type ReadinessReport =
  | RenderArmyReadinessReportToolOutput
  | RenderNavyReadinessReportToolOutput
  | RenderSpaceForceReadinessReportToolOutput;

type ReportTheme = {
  accent: string;
  border: string;
  gold: string;
  panelBackground: string;
};

type ChartSectionProps = {
  children: ReactNode;
  className?: string;
  description?: string;
  tag?: string;
  theme?: ReportTheme;
  title: string;
};

const TIER_ORDER: NavyReadinessTier[] = ['Field', 'Pilot', 'Watch', 'Pass'];

const TIER_COLORS: Record<NavyReadinessTier, string> = {
  Field: '#1f8a4c',
  Pass: '#9aa3b2',
  Pilot: '#6f42c1',
  Watch: '#d97706',
};

const compactTechLabel = (tech: string): string => {
  const [firstToken] = tech.split(' ');

  return firstToken ?? tech;
};

const formatNumber = (value: number): string => {
  return Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
};

const isArmyReport = (report: ReadinessReport): report is RenderArmyReadinessReportToolOutput => {
  const [country] = report.charts.countryMix;

  return country !== undefined && 'armyCoa' in country;
};

const isSpaceForceReport = (report: ReadinessReport): report is RenderSpaceForceReadinessReportToolOutput => {
  return 'missionCoverage' in report.charts;
};

const getReportTheme = (report: ReadinessReport): ReportTheme => {
  if (isArmyReport(report)) {
    return {
      accent: '#4b5320',
      border: '#d8dece',
      gold: '#cfb53b',
      panelBackground: '#f5f6f2',
    };
  }

  return {
    accent: '#0b2545',
    border: '#e5e7eb',
    gold: '#c9a227',
    panelBackground: '#f8fafc',
  };
};

const ChartSection: FC<ChartSectionProps> = (props) => {
  const { children, className, description, tag, theme, title } = props;

  return (
    <section
      className={cn('rounded-lg border bg-white p-4 shadow-sm', className)}
      style={{ borderColor: theme?.border }}
    >
      <div
        className="mb-3 flex flex-col gap-1 border-b pb-2"
        style={{ borderColor: theme?.border }}
      >
        <div className="flex items-center justify-between gap-3">
          <h3
            className="text-xs font-semibold uppercase tracking-wide text-gray-900"
            style={{ color: theme?.accent }}
          >
            {title}
          </h3>
          {tag && (
            <span
              className="rounded px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white"
              style={{ backgroundColor: theme?.accent }}
            >
              {tag}
            </span>
          )}
        </div>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
      {children}
    </section>
  );
};

const renderTierBadge = (tier: NavyReadinessTier): ReactNode => {
  return (
    <span
      className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase text-white"
      style={{ backgroundColor: TIER_COLORS[tier] }}
    >
      {tier}
    </span>
  );
};

const KpiStrip: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);
  const kpis = [
    {
      ...report.kpis.applicants,
      sublabel: isArmy
        ? `${report.meta.nations} nations - ${report.meta.techAreas} tech areas`
        : report.kpis.applicants.sublabel,
    },
    {
      ...report.kpis.field,
      label: isArmy ? 'Transition-Ready (TR)' : report.kpis.field.label,
      sublabel: isArmy ? 'Composite >= 72 - ARL-validated' : report.kpis.field.sublabel,
    },
    {
      ...report.kpis.pilot,
      label: isArmy ? 'Pilot / OTA Ready' : report.kpis.pilot.label,
    },
    report.kpis.watch,
    report.kpis.pass,
    {
      ...report.kpis.coalitionEligible,
      label: isArmy ? 'Five-Eyes / Coalition Eligible' : report.kpis.coalitionEligible.label,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {kpis.map((kpi) => (
        <div
          className="rounded-lg border bg-white p-4 shadow-sm"
          key={kpi.label}
          style={{ borderColor: theme.border }}
        >
          <div className="text-xs font-medium uppercase text-gray-500">{kpi.label}</div>
          <div
            className="mt-1 text-2xl font-semibold"
            style={{ color: kpi.color }}
          >
            {formatNumber(kpi.value)}
          </div>
          <div className="mt-1 text-xs text-gray-500">{kpi.sublabel}</div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full"
              style={{ backgroundColor: kpi.color, width: `${kpi.percent}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const TechMixChart: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const chartHeight = Math.max(360, report.charts.techMix.length * 28);
  const yAxisWidth = report.charts.techMix.length > 12 ? 190 : 120;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);

  return (
    <ChartSection
      tag={isArmy ? '12 Army priority areas' : undefined}
      theme={theme}
      title="Core Technology Mix"
    >
      <div style={{ height: chartHeight }}>
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart
            data={report.charts.techMix}
            layout="vertical"
            margin={{ bottom: 8, left: 24, right: 24, top: 8 }}
          >
            <CartesianGrid
              horizontal={false}
              stroke="#eef1f7"
            />
            <XAxis
              allowDecimals={false}
              type="number"
            />
            <YAxis
              dataKey="tech"
              tick={{ fontSize: 11 }}
              type="category"
              width={yAxisWidth}
            />
            <Tooltip />
            <Bar
              dataKey="count"
              radius={4}
            >
              {report.charts.techMix.map((item) => (
                <Cell
                  fill={item.color}
                  key={item.tech}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const CountryMixChart: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);

  return (
    <ChartSection
      tag={isArmy ? '11 nations' : undefined}
      theme={theme}
      title="Country of Origin"
    >
      <div className="h-[300px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <PieChart>
            <Pie
              cx="45%"
              cy="50%"
              data={report.charts.countryMix}
              dataKey="count"
              innerRadius={62}
              nameKey="country"
              outerRadius={98}
              paddingAngle={1}
            >
              {report.charts.countryMix.map((item) => (
                <Cell
                  fill={item.color}
                  key={item.country}
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend
              align="right"
              layout="vertical"
              verticalAlign="middle"
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const InventoryDepthChart: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);
  const data = report.charts.inventoryDepth.map((item) => ({
    ...item,
    label: compactTechLabel(item.tech),
  }));

  return (
    <ChartSection
      tag={isArmy ? 'depth' : undefined}
      theme={theme}
      title={isArmy ? 'Product -> System -> Subsystem Inventory' : 'Product / System / Subsystem Inventory'}
    >
      <div className="h-[300px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart
            data={data}
            margin={{ bottom: 20, left: 8, right: 20, top: 8 }}
          >
            <CartesianGrid
              stroke="#eef1f7"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
            />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="products"
              fill={isArmy ? '#4b5320' : '#0b2545'}
              name="Products"
              stackId="inventory"
            />
            <Bar
              dataKey="systemsExtra"
              fill="#0f8a8d"
              name="Systems"
              stackId="inventory"
            />
            <Bar
              dataKey="subsystemsExtra"
              fill="#c9a227"
              name="Subsystems"
              radius={[4, 4, 0, 0]}
              stackId="inventory"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const TrlDistributionChart: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);
  const data = report.charts.trlDistribution.map((item) => ({
    ...item,
    label: `TRL ${item.bucket}`,
  }));

  return (
    <ChartSection
      description={isArmy ? undefined : 'Current TRL, 24-month projection, and median velocity by TRL bucket.'}
      tag={isArmy ? 'AR 70-1 / DoD 5000.02' : undefined}
      theme={theme}
      title={isArmy ? 'TRL Distribution - Current vs. Projected (24 mo)' : 'TRL Distribution'}
    >
      <div className="h-[320px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <ComposedChart
            data={data}
            margin={{ bottom: 8, left: 8, right: 8, top: 8 }}
          >
            <CartesianGrid stroke="#eef1f7" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              yAxisId="count"
            />
            <YAxis
              domain={[0, 2]}
              orientation="right"
              tick={{ fontSize: 11 }}
              yAxisId="velocity"
            />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="current"
              fill={isArmy ? '#3a4118' : '#13315c'}
              name="Current"
              radius={4}
              yAxisId="count"
            />
            <Bar
              dataKey="projected"
              fill="#0f8a8d"
              name="Projected (24 mo)"
              radius={4}
              yAxisId="count"
            />
            <Line
              dataKey="medianVelocity"
              name="Median Velocity"
              stroke="#c9a227"
              strokeWidth={2}
              type="monotone"
              yAxisId="velocity"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const MrlPostureChart: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);
  const data = report.charts.mrlPosture.map((item) => ({
    ...item,
    label: compactTechLabel(item.tech),
  }));

  return (
    <ChartSection
      tag={isArmy ? 'DoD MRL Deskbook / GFMAP' : undefined}
      theme={theme}
      title="MRL Posture"
    >
      <div className="h-[300px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart
            data={data}
            margin={{ bottom: 20, left: 8, right: 20, top: 8 }}
          >
            <CartesianGrid
              stroke="#eef1f7"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11 }}
            />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="preLrip"
              fill={isArmy ? '#cfd6c8' : '#cfd6e3'}
              name="Pre-LRIP"
              stackId="mrl"
            />
            <Bar
              dataKey="lrip"
              fill={isArmy ? '#7a8a6a' : '#7a8aa6'}
              name="LRIP"
              stackId="mrl"
            />
            <Bar
              dataKey="frpReady"
              fill="#0f8a8d"
              name="FRP-Ready"
              stackId="mrl"
            />
            <Bar
              dataKey="frpDemo"
              fill="#1f8a4c"
              name="FRP-Demo"
              radius={[4, 4, 0, 0]}
              stackId="mrl"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const CyberHistogramChart: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);

  return (
    <ChartSection
      description={
        isArmy
          ? 'Composite cyber posture combines CMMC level, SBOM coverage, Army Zero Trust readiness, CVE hygiene, and supply-chain attestation.'
          : undefined
      }
      tag={isArmy ? 'CMMC 2.0 / RMF / Army ZT' : undefined}
      theme={theme}
      title="Cyber Posture Composite"
    >
      <div className="h-[260px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart
            data={report.charts.cyberHistogram.bins}
            margin={{ bottom: 8, left: 8, right: 20, top: 8 }}
          >
            <CartesianGrid
              stroke="#eef1f7"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11 }}
            />
            <Tooltip />
            <Bar
              dataKey="count"
              radius={4}
            >
              {report.charts.cyberHistogram.bins.map((item) => (
                <Cell
                  fill={item.color}
                  key={item.label}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const PriorityMatrixChart: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);

  return (
    <ChartSection
      className="lg:col-span-2"
      description="Bubble size reflects product depth. Color reflects composite tier."
      tag={isArmy ? '2x2' : undefined}
      theme={theme}
      title={
        isArmy
          ? 'Prioritization Matrix - Maturity x Army Strategic Fit'
          : 'Prioritization Matrix - Maturity x Strategic Fit'
      }
    >
      <div className="relative h-[420px]">
        <div className="pointer-events-none absolute left-8 top-8 z-10 text-xs font-semibold text-gray-400">
          STRATEGIC BETS
        </div>
        <div className="pointer-events-none absolute right-8 top-8 z-10 text-xs font-semibold text-gray-400">
          {isArmy ? 'TRANSITION-READY' : 'FIELD NOW'}
        </div>
        <div className="pointer-events-none absolute bottom-8 left-8 z-10 text-xs font-semibold text-gray-400">
          DEFER / PASS
        </div>
        <div className="pointer-events-none absolute bottom-8 right-8 z-10 text-xs font-semibold text-gray-400">
          QUICK WINS
        </div>
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <ScatterChart margin={{ bottom: 16, left: 8, right: 16, top: 8 }}>
            <CartesianGrid stroke="#eef1f7" />
            <XAxis
              dataKey="maturityIndex"
              domain={[30, 100]}
              name="Maturity Index"
              tick={{ fontSize: 11 }}
              type="number"
            />
            <YAxis
              dataKey="strategicFit"
              domain={[30, 100]}
              name={isArmy ? 'Army Modernization Fit' : 'Strategic Fit'}
              tick={{ fontSize: 11 }}
              type="number"
            />
            <ZAxis
              dataKey="radius"
              range={[40, 180]}
            />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Legend />
            {TIER_ORDER.map((tier) => (
              <Scatter
                data={report.charts.priorityMatrix.filter((item) => item.tier === tier)}
                fill={TIER_COLORS[tier]}
                key={tier}
                name={tier}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const ScoreHistogramChart: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);

  return (
    <ChartSection
      description={
        isArmy
          ? 'Composite = 0.25 TRL + 0.20 MRL + 0.18 Cyber + 0.14 Velocity + 0.10 Research Foundation + 0.13 Army Modernization Fit.'
          : 'Composite score drives Field, Pilot, Watch, and Pass tiers.'
      }
      tag={isArmy ? '100 cos.' : undefined}
      theme={theme}
      title="Composite Score Distribution"
    >
      <div className="h-[260px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart
            data={report.charts.scoreHistogram.bins}
            margin={{ bottom: 8, left: 8, right: 20, top: 8 }}
          >
            <CartesianGrid
              stroke="#eef1f7"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11 }}
            />
            <Tooltip />
            <Bar
              dataKey="count"
              radius={4}
            >
              {report.charts.scoreHistogram.bins.map((item) => (
                <Cell
                  fill={item.color}
                  key={item.label}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const MissionCoverageChart: FC<{ report: RenderSpaceForceReadinessReportToolOutput }> = (props) => {
  const { report } = props;
  const missions = [...new Set(report.charts.missionCoverage.map((row) => row.mission))];
  const techs = report.charts.techMix.map((row) => row.tech);
  const maxValue = Math.max(...report.charts.missionCoverage.map((row) => row.value), 1);

  return (
    <ChartSection
      className="lg:col-span-3"
      description="Weighted count of AI capability coverage by USSF mission thread."
      title="USSF Mission Thread Coverage"
    >
      <div className="overflow-auto">
        <div
          className="min-w-[980px] text-xs"
          style={{ display: 'grid', gridTemplateColumns: `190px repeat(${missions.length}, minmax(86px, 1fr))` }}
        >
          <div className="sticky left-0 z-10 border-b bg-white p-2 font-semibold text-gray-500">AI area</div>
          {missions.map((mission) => (
            <div
              className="border-b p-2 text-center font-semibold text-gray-500"
              key={mission}
            >
              {mission}
            </div>
          ))}
          {techs.map((tech) => {
            const cells = missions.map((mission) => {
              return report.charts.missionCoverage.find((row) => row.mission === mission && row.tech === tech);
            });

            return (
              <Fragment key={tech}>
                <div className="sticky left-0 z-10 border-b bg-white p-2 font-medium text-gray-700">{tech}</div>
                {cells.map((cell, index) => {
                  const value = cell?.value ?? 0;
                  const opacity = Math.max(0.08, Math.min(0.85, value / maxValue));
                  const mission = missions[index] ?? 'unknown';

                  return (
                    <div
                      className="border-b border-l p-2 text-center font-semibold text-gray-800"
                      key={`${tech}-${mission}`}
                      style={{ backgroundColor: `rgba(15, 138, 141, ${opacity})` }}
                    >
                      {value.toFixed(1)}
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      </div>
    </ChartSection>
  );
};

const ShortlistTable: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);

  return (
    <ChartSection
      className="lg:col-span-2"
      tag={isArmy ? 'top 18 of 100' : undefined}
      theme={theme}
      title={
        isArmy
          ? 'Recommended Shortlist - Transition-Ready and Pilot Tiers'
          : 'Recommended Shortlist - Field-Now and Pilot Tiers'
      }
    >
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full min-w-[900px] text-left text-xs">
          <thead className="sticky top-0 bg-white text-gray-500">
            <tr className="border-b">
              <th className="p-2">#</th>
              <th className="p-2">Company</th>
              <th className="p-2">Origin</th>
              <th className="p-2">Tech</th>
              <th className="p-2">P/S/Sub</th>
              <th className="p-2">TRL</th>
              <th className="p-2">24 mo</th>
              <th className="p-2">Vel.</th>
              <th className="p-2">MRL</th>
              <th className="p-2">Cyber</th>
              <th className="p-2">Score</th>
              <th className="p-2">Tier</th>
            </tr>
          </thead>
          <tbody>
            {report.tables.shortlist.map((row) => (
              <tr
                className="border-b last:border-b-0"
                key={`${row.rank}-${row.company}`}
              >
                <td className="p-2 font-medium">{row.rank}</td>
                <td className="p-2 font-semibold text-gray-900">{row.company}</td>
                <td className="p-2">{row.country}</td>
                <td className="p-2">{row.tech}</td>
                <td className="p-2">
                  {row.products}/{row.systems}/{row.subsystems}
                </td>
                <td className="p-2">{row.trl}</td>
                <td className="p-2">{row.projectedTrl24Mo.toFixed(1)}</td>
                <td className="p-2">{row.velocity.toFixed(2)}</td>
                <td className="p-2">{row.mrl}</td>
                <td className="p-2">
                  <div className="flex min-w-[90px] items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full"
                        style={{
                          backgroundColor: row.cyber >= 80 ? '#1f8a4c' : row.cyber >= 60 ? '#d97706' : '#b42318',
                          width: `${row.cyber}%`,
                        }}
                      />
                    </div>
                    <span>{row.cyber}</span>
                  </div>
                </td>
                <td className="p-2 font-semibold">{row.score.toFixed(1)}</td>
                <td className="p-2">{renderTierBadge(row.tier)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartSection>
  );
};

const RiskWatchTable: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);

  return (
    <ChartSection
      tag={isArmy ? 'bottom decile' : undefined}
      theme={theme}
      title={isArmy ? 'Risk / Defer Watch' : 'Risk and Defer Watch'}
    >
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full min-w-[620px] text-left text-xs">
          <thead className="sticky top-0 bg-white text-gray-500">
            <tr className="border-b">
              <th className="p-2">Company</th>
              <th className="p-2">Origin</th>
              <th className="p-2">Tech</th>
              <th className="p-2">TRL</th>
              <th className="p-2">MRL</th>
              <th className="p-2">Cyber</th>
              <th className="p-2">Primary risk</th>
            </tr>
          </thead>
          <tbody>
            {report.tables.riskWatch.map((row) => (
              <tr
                className="border-b last:border-b-0"
                key={`${row.company}-${row.tech}`}
              >
                <td className="p-2 font-semibold text-gray-900">{row.company}</td>
                <td className="p-2">{row.country}</td>
                <td className="p-2">{row.tech}</td>
                <td className="p-2">{row.trl}</td>
                <td className="p-2">{row.mrl}</td>
                <td className="p-2">{row.cyber}</td>
                <td className="p-2 text-gray-600">{row.primaryRisk}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartSection>
  );
};

export const NavyReadinessReportArtifact: FC<NavyReadinessReportArtifactProps> = (props) => {
  const { report } = props;
  const isArmy = isArmyReport(report);
  const theme = getReportTheme(report);

  return (
    <div
      className="flex flex-col gap-4 p-4"
      style={{ backgroundColor: isArmy ? theme.panelBackground : undefined }}
    >
      {isArmy ? (
        <Fragment>
          <div
            className="rounded py-1 text-center text-[11px] font-bold uppercase tracking-[0.22em] text-white"
            style={{ backgroundColor: theme.accent }}
          >
            Unclassified // For Official Use Only - Illustrative Sample
          </div>
          <header
            className="relative rounded-lg border bg-white p-5 shadow-sm"
            style={{ borderBottomColor: theme.accent, borderBottomWidth: 4, borderColor: theme.border }}
          >
            <div
              className="absolute bottom-[-7px] left-0 right-0 h-0.5"
              style={{ backgroundColor: theme.gold }}
            />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-4">
                <div
                  className="flex size-[72px] shrink-0 flex-col items-center justify-center rounded-full border-2 text-center text-[10px] font-bold uppercase leading-tight text-white"
                  style={{ backgroundColor: theme.accent, borderColor: theme.gold }}
                >
                  <span>United States</span>
                  <span className="text-sm">Army</span>
                </div>
                <div>
                  <h2
                    className="text-lg font-semibold"
                    style={{ color: theme.accent }}
                  >
                    Department of the Army - Pathway to Innovative Technology (PIT) - Applicant Portfolio Readiness
                    Assessment
                  </h2>
                  <p className="mt-1 text-xs text-gray-600">
                    Cohort: {report.meta.applicants} applicants - Cycle: {report.meta.cycle} - Methodology:{' '}
                    {report.meta.methodologyLabel} - Army Modernization Priority Alignment - AR 70-1 / ADP 9-0
                  </p>
                </div>
              </div>
              <div className="text-left text-xs text-gray-600 lg:text-right">
                <div className="font-bold text-[#062726]">Azymmetric - TYR Platform</div>
                <div className="mt-1">Doc ID: ARMY-PIT-26Q3-100-AZ</div>
                <div>Sponsor: ASA(ALT) / PIT Program Office</div>
              </div>
            </div>
          </header>
        </Fragment>
      ) : (
        <header className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase text-[#3D2EFF]">{report.meta.syntheticDataLabel}</div>
              <h2 className="mt-1 text-xl font-semibold text-gray-950">{report.meta.title}</h2>
              <p className="mt-1 text-sm text-gray-500">
                {report.meta.sponsor} · {report.meta.cycle} · {report.meta.methodologyLabel}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-right text-xs text-gray-500">
              <div>
                <div className="text-lg font-semibold text-gray-900">{report.meta.applicants}</div>
                Applicants
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{report.meta.nations}</div>
                Nations
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">{report.meta.techAreas}</div>
                Tech areas
              </div>
            </div>
          </div>
        </header>
      )}

      <KpiStrip report={report} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <TechMixChart report={report} />
        <CountryMixChart report={report} />
        <InventoryDepthChart report={report} />
        <TrlDistributionChart report={report} />
        <MrlPostureChart report={report} />
        <CyberHistogramChart report={report} />
        <PriorityMatrixChart report={report} />
        <ScoreHistogramChart report={report} />
        {isSpaceForceReport(report) && <MissionCoverageChart report={report} />}
        <ShortlistTable report={report} />
        <RiskWatchTable report={report} />
      </div>
      {isArmy && (
        <div
          className="rounded py-1 text-center text-[11px] font-bold uppercase tracking-[0.22em] text-white"
          style={{ backgroundColor: theme.accent }}
        >
          Unclassified // For Official Use Only - Illustrative Sample
        </div>
      )}
    </div>
  );
};
